import { useAuth } from "@/src/contexts/auth.context";
import { useUploadFile } from "@/src/hooks/features/use-upload-file";
import { PickedFile } from "@/src/models/file.models";
import {
  ConfirmModalState,
  InfoModalState,
  ModalVariant,
  SubmitArticleParams,
  WikiFormData,
} from "@/src/models/wiki.models";
import { USER_PROFILE_QUERY_KEYS } from "@/src/query-keys/users.keys";
import {
  WIKI_ARTICLE_QUERY_KEY,
  WIKI_ARTICLES_BY_CATEGORY_QUERY_KEY,
  WIKI_CATEGORIES_QUERY_KEY,
  WIKI_DRAFT_ARTICLES_QUERY_KEY,
  WIKI_SAVED_ARTICLES_QUERY_KEY,
} from "@/src/query-keys/wiki.keys";
import { sendNotification } from "@/src/services/api/notification/notification.service";
import {
  ensureUserProfile,
  useGetUserProfileQuery,
} from "@/src/services/profile/profile.hooks";
import { UPDATE_USER_PROFILE } from "@/src/services/profile/profile.query";
import { useWikiCategoriesQuery } from "@/src/services/wiki/wiki.hooks";
import {
  buildMergedAttachments,
  gqlRequest,
  saveArticleToGraphQL,
  uploadCoverImageIfLocal,
} from "@/src/services/wiki/wiki.services";
import { stripHtml } from "@/src/utils/html.utils";
import { parseParamAttachments } from "@/src/utils/wiki.utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export function useWikiCreation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.itemId ?? "";

  const { mutateAsync: uploadImage } = useUploadFile();
  const { data: profile } = useGetUserProfileQuery(userId);

  const params = useLocalSearchParams<{
    draftId?: string;
    Title?: string;
    title?: string;
    Content?: string;
    content?: string;
    categoryId?: string;
    coverImageUrl?: string;
    coverImage?: string;
    attachments?: string;
  }>();

  const draftId = params.draftId;
  const paramTitle = params.Title ?? "";
  const paramContent = params.Content ?? "";
  const paramCategoryId = params.categoryId;
  const paramCoverImage = params.coverImageUrl ?? params.coverImage;
  const paramAttachments = params.attachments;

  const isEditing = !!draftId;

  const { data: categoryData = [] } = useWikiCategoriesQuery();
  const categoryDataRef = useRef(categoryData);
  categoryDataRef.current = categoryData;

  const form = useForm<WikiFormData>({
    defaultValues: {
      Title: paramTitle,
      Content: paramContent,
    },
  });

  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const selectedCategoryIdsRef = useRef<string[]>([]);
  selectedCategoryIdsRef.current = selectedCategoryIds;

  const [coverImage, setCoverImage] = useState<PickedFile>(
    paramCoverImage
      ? ({ uri: paramCoverImage } as PickedFile)
      : (null as unknown as PickedFile),
  );

  const [attachments, setAttachments] = useState<PickedFile[]>(() =>
    parseParamAttachments(paramAttachments),
  );

  useEffect(() => {
    if (!paramCategoryId || categoryData.length === 0) return;
    const exists = categoryData.some(
      (cat) => cat.categoryID === paramCategoryId,
    );
    if (exists) setSelectedCategoryIds([paramCategoryId]);
  }, [paramCategoryId, categoryData]);

  const [infoModal, setInfoModal] = useState<InfoModalState>({
    visible: false,
    title: "",
    description: "",
    variant: "info",
    onConfirm: () => dismissInfoModal(),
    confirmText: undefined,
  });

  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    title: "",
    description: "",
    confirmText: "",
    cancelText: "",
    onConfirm: () => {},
    onCancel: () => dismissConfirmModal(),
  });

  const dismissInfoModal = () =>
    setInfoModal((prev) => ({ ...prev, visible: false }));

  const dismissConfirmModal = () =>
    setConfirmModal((prev) => ({ ...prev, visible: false }));

  const showInfo = (
    title: string,
    description: string,
    variant: ModalVariant = "info",
    onOk?: () => void,
  ) =>
    setInfoModal({
      visible: true,
      title,
      description,
      variant,
      confirmText: undefined,
      onConfirm: () => {
        dismissInfoModal();
        onOk?.();
      },
    });

  const showConfirm = (
    title: string,
    description: string,
    onConfirm: () => void,
    confirmText = t("BTN_YES"),
    cancelText = t("BTN_NO"),
  ) =>
    setConfirmModal({
      visible: true,
      title,
      description,
      confirmText,
      cancelText,
      onConfirm: () => {
        dismissConfirmModal();
        onConfirm();
      },
      onCancel: dismissConfirmModal,
    });

  const updateUserProfileTracking = useCallback(
    async (articleItemId: string, isDraft: boolean): Promise<void> => {
      if (!articleItemId || !userId) return;

      // The draft id is recorded on the user's profile row, and UPDATE_USER_PROFILE
      // matches on CreatedBy — with no row it updates nothing, reports success, and
      // the draft never appears in the Drafts tab. Guarantee the row first, and read
      // the current ids from it rather than from a profile query that may not have
      // resolved when this runs straight after a create.
      const freshProfile = await ensureUserProfile(userId);
      const currentDraftIds = [
        ...(freshProfile?.wikiDraftIds ?? profile?.wikiDraftIds ?? []),
      ];

      const updatedDraftIds = isDraft
        ? currentDraftIds.includes(articleItemId)
          ? currentDraftIds
          : [...currentDraftIds, articleItemId]
        : currentDraftIds.filter((id) => id !== articleItemId);

      await gqlRequest(UPDATE_USER_PROFILE, {
        createdBy: userId,
        wikiDraftIds: updatedDraftIds,
      });

      queryClient.invalidateQueries({
        queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
      });
    },
    [profile, userId, queryClient],
  );

  const { mutate: submitArticle, isPending: isSubmitting } = useMutation({
    mutationFn: async ({ data, isDraft, categoryId }: SubmitArticleParams) => {
      const coverImageUrl = await uploadCoverImageIfLocal(
        coverImage,
        uploadImage,
      );
      const mergedAttachments = await buildMergedAttachments(attachments);

      const articleInput = {
        Title: data.Title,
        Content: data.Content,
        coverImageUrl,
        categoryIds: categoryId,
        attachmentUrls: mergedAttachments,
        isDraft,
        authorSnapshot: {
          firstName: user?.firstName ?? "",
          lastName: user?.lastName ?? "",
          email: user?.email ?? "",
          profileImageUrl: user?.profileImageUrl ?? "",
        },
      };

      const articleItemId = await saveArticleToGraphQL(
        data,
        coverImageUrl,
        mergedAttachments,
        categoryId,
        isDraft,
        articleInput,
        isEditing,
        draftId,
      );

      await updateUserProfileTracking(articleItemId, isDraft);

      // Never let notifying fail the write: the article is already saved by this
      // point, so a rejection here would report failure for a successful create.
      // `sendNotification` also resolves rather than rejects on any failure, so
      // this try/catch is belt-and-braces rather than the sole guard.
      if (!isDraft && articleItemId) {
        try {
          await sendNotification({
            userIds: [""],
            configurationName: isEditing ? "wiki-updated" : "wiki-created",
            denormalizedPayload: JSON.stringify({
              type: isEditing ? "wiki_article_updated" : "wiki_article_created",
              message: isEditing
                ? `Wiki article updated: "${data.Title}"`
                : `New wiki article published: "${data.Title}"`,
              actorAvatar: user?.profileImageUrl ?? "",
              actorName:
                `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim(),
              actorId: user?.itemId ?? "",
              targetEntityId: articleItemId,
            }),
            responseKey: "wiki-action",
            responseValue: isEditing
              ? "wiki_article_updated"
              : "wiki_article_published",
          });
        } catch {
          // notification failure is not a create/edit failure
        }
      }

      // Knowledge-base ingestion (chatbot indexing) is deliberately skipped.
      // It ran here as the last awaited step of the create, which meant a slow
      // or failing ingest held up the "Article created" confirmation and, on
      // rejection, reported the whole publish as failed even though the article
      // was already saved. Publishing now completes as soon as the article is
      // written; `ingestArticleToKnowledgeBase` is kept in the wiki service for
      // whenever indexing is reintroduced as a background job.
    },

    onSuccess: (_, { isDraft }) => {
      queryClient.invalidateQueries({ queryKey: WIKI_ARTICLE_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: WIKI_SAVED_ARTICLES_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: WIKI_DRAFT_ARTICLES_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: WIKI_ARTICLES_BY_CATEGORY_QUERY_KEY,
      });
      queryClient.invalidateQueries({ queryKey: WIKI_CATEGORIES_QUERY_KEY });
      showInfo(
        t("SUCCESS"),
        isDraft
          ? t("SUCCESS_ARTICLE_SAVED_DRAFT")
          : t("SUCCESS_ARTICLE_CREATED"),
        "success",
        () => router.replace("/(tabs)/wiki/wiki-articles"),
      );
    },

    onError: () => {
      showInfo(t("ERROR"), t("ERROR_GENERIC"), "error");
    },
  });

  const validateForPublish = (data: WikiFormData): boolean => {
    if (!data.Title?.trim()) {
      showInfo(t("ERROR"), t("ERROR_HEADLINE_BLANK"), "error");
      return false;
    }
    if (!stripHtml(data.Content ?? "")) {
      showInfo(t("ERROR"), t("ERROR_CONTENT_BLANK"), "error");
      return false;
    }
    if (!selectedCategoryIdsRef.current[0]) {
      showInfo(t("ERROR"), t("ERROR_CATEGORY_BLANK"), "error");
      return false;
    }
    // Cover image is optional — publishing no longer blocks on it.
    return true;
  };

  const onSubmit = (data: WikiFormData, isDraft: boolean) => {
    if (isDraft) {
      submitArticle({
        data,
        isDraft,
        categoryId: selectedCategoryIdsRef.current[0] ?? "",
      });
      return;
    }

    if (!validateForPublish(data)) return;

    submitArticle({
      data,
      isDraft,
      categoryId: selectedCategoryIdsRef.current[0],
    });
  };

  const handleCreate = form.handleSubmit((data) => onSubmit(data, false));
  const handleSaveDraft = form.handleSubmit((data) => onSubmit(data, true));

  const handleCancel = () =>
    showConfirm(
      t("BTN_CANCEL"),
      t("QUEST_CANCEL_ARTICLE_CREATION"),
      () => router.replace("/(tabs)/wiki/wiki-articles"),
      t("BTN_YES"),
      t("BTN_CANCEL"),
    );

  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const WIKI_ATTACHMENT_MIME_TYPES = ["application/pdf", "text/plain"];

  const handleAddAttachments = (files: PickedFile[]) => {
    setAttachmentError(null);
    setAttachments((prev) => [...prev, ...files]);
  };

  const handleRemoveAttachment = (index: number) =>
    setAttachments((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });

  return {
    form,
    selectedCategoryIds,
    setSelectedCategoryIds,
    coverImage,
    setCoverImage,
    attachments,
    handleAddAttachments,
    handleRemoveAttachment,
    infoModal,
    confirmModal,
    handleCreate,
    handleSaveDraft,
    handleCancel,
    isEditing,
    isSubmitting,
    attachmentError,
    setAttachmentError,
    WIKI_ATTACHMENT_MIME_TYPES,
  };
}
